import React, { useState, useEffect } from 'react';
import { studentSharing, notes } from '../../api';
import ShareStudentModal from './ShareStudentModal';
import '../Sharing/CoachStudentSharingTab.css';

/**
 * CoachStudentSharingTab - Manage student sharing for coaches
 * Shows shares sent BY this coach and shares received FROM other coaches
 */
const CoachStudentSharingTab = () => {
  const [sentShares, setSentShares] = useState([]);
  const [receivedShares, setReceivedShares] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState('sent');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load shares
      const sharesResponse = await studentSharing.getMyShares();
      setSentShares(sharesResponse.data.sentShares || []);
      setReceivedShares(sharesResponse.data.receivedShares || []);

      // Load students for the modal
      const notesResponse = await notes.getStudents();
      setStudents(notesResponse.data || []);
    } catch (err) {
      console.error('Error loading shares:', err);
      setError('Failed to load sharing data');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeShare = async (shareId, coachName) => {
    if (!window.confirm(`Revoke access from ${coachName}?`)) {
      return;
    }

    try {
      await studentSharing.revokeShare(shareId);
      setSentShares(sentShares.filter(s => s._id !== shareId));
    } catch (err) {
      alert('Failed to revoke share: ' + err.response?.data?.message);
    }
  };

  const handleAcceptShare = async (shareId) => {
    try {
      await studentSharing.acceptShare(shareId);
      // Move from pending to accepted
      const share = receivedShares.find(s => s._id === shareId);
      if (share) {
        share.status = 'accepted';
      }
      setReceivedShares([...receivedShares]);
    } catch (err) {
      alert('Failed to accept share: ' + err.response?.data?.message);
    }
  };

  const handleRejectShare = async (shareId) => {
    try {
      await studentSharing.rejectShare(shareId);
      setReceivedShares(receivedShares.filter(s => s._id !== shareId));
    } catch (err) {
      alert('Failed to reject share: ' + err.response?.data?.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending">⏳ Pending</span>;
      case 'accepted':
        return <span className="status-badge accepted">✅ Accepted</span>;
      case 'revoked':
        return <span className="status-badge revoked">🔒 Revoked</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  if (loading) {
    return <div className="sharing-tab">Loading...</div>;
  }

  return (
    <div className="sharing-tab">
      <div className="sharing-header">
        <h2>🤝 Coaching Partnerships</h2>
        <button
          className="share-btn"
          onClick={() => setShowShareModal(true)}
        >
          + Share Student
        </button>
      </div>

      {error && (
        <div className="sharing-error">
          ❌ {error}
        </div>
      )}

      <div className="sharing-tabs">
        <button
          className={`tab-btn ${activeTab === 'sent' ? 'active' : ''}`}
          onClick={() => setActiveTab('sent')}
        >
          Shared By Me ({sentShares.length})
        </button>
        <button
          className={`tab-btn ${activeTab === 'received' ? 'active' : ''}`}
          onClick={() => setActiveTab('received')}
        >
          Shared With Me ({receivedShares.length})
        </button>
      </div>

      {activeTab === 'sent' && (
        <div className="sharing-section">
          {sentShares.length === 0 ? (
            <div className="empty-state">
              <p>You haven't shared any students yet.</p>
              <p>Click "Share Student" to start collaborating with other coaches.</p>
            </div>
          ) : (
            <div className="sharing-list">
              {sentShares.map(share => (
                <div key={share._id} className="sharing-card">
                  <div className="sharing-card-header">
                    <div>
                      <h4 className="sharing-coach-name">
                        📤 {share.coachName || share.coachEmail}
                      </h4>
                      <p className="sharing-student-name">
                        Student: <strong>{share.studentId}</strong>
                      </p>
                    </div>
                    {getStatusBadge(share.status)}
                  </div>
                  <div className="sharing-card-details">
                    <small>
                      {share.status === 'pending' && (
                        <>Invited {formatDate(share.invitedAt)}</>
                      )}
                      {share.status === 'accepted' && (
                        <>Accepted {formatDate(share.acceptedAt)}</>
                      )}
                    </small>
                  </div>
                  {share.status !== 'revoked' && (
                    <button
                      className="revoke-btn"
                      onClick={() => handleRevokeShare(share._id, share.coachName || share.coachEmail)}
                    >
                      🔒 Revoke Access
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'received' && (
        <div className="sharing-section">
          {receivedShares.length === 0 ? (
            <div className="empty-state">
              <p>No coaches have shared students with you yet.</p>
              <p>When another coach shares a student, they'll appear here.</p>
            </div>
          ) : (
            <div className="sharing-list">
              {receivedShares.map(share => (
                <div key={share._id} className="sharing-card received">
                  <div className="sharing-card-header">
                    <div>
                      <h4 className="sharing-coach-name">
                        📥 {share.fromCoachName}
                      </h4>
                      <p className="sharing-student-name">
                        Student: <strong>{share.studentId}</strong>
                      </p>
                    </div>
                    {getStatusBadge(share.status)}
                  </div>
                  <div className="sharing-card-details">
                    <small>
                      Shared {formatDate(share.invitedAt)}
                    </small>
                  </div>
                  {share.status === 'pending' && (
                    <div className="sharing-actions">
                      <button
                        className="accept-btn"
                        onClick={() => handleAcceptShare(share._id)}
                      >
                        ✅ Accept
                      </button>
                      <button
                        className="reject-btn"
                        onClick={() => handleRejectShare(share._id)}
                      >
                        ❌ Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ShareStudentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        students={students}
        onShareSuccess={loadData}
      />
    </div>
  );
};

export default CoachStudentSharingTab;
